import classNames from 'classnames';
import * as React from 'react';
import { Button } from 'palette/Button';
import { usePaletteConfig } from 'palette/Config';
import { IconName } from 'palette/Icon';
import { useModal } from 'palette/Modal/hooks';
import { Small, Header, BodyText } from 'palette/Typography';
import './styles/ModalBoxHeader.scss';

type ModalBoxHeaderProps = React.PropsWithChildren<{
    title?: string;
    titleAlign?: 'left' | 'center';
    subtitle?: string;
    leftActionIcon?: IconName;
    onLeftActionClick?: () => void;
    className?: string;
    onClose?(): void;
}>;

function ModalBoxHeader({
    title,
    titleAlign = 'left',
    subtitle,
    children,
    className,
    leftActionIcon,
    onLeftActionClick,
    onClose,
}: ModalBoxHeaderProps) {
    const { translate } = usePaletteConfig();
    const { setActiveModalId } = useModal();

    const handleCloseButtonClick = () => {
        setActiveModalId(null);
        onClose?.();
    };

    return (
        <header className={classNames('palette-modal-box-header', className)}>

            {onLeftActionClick && (
                <Button
                    isTransparent
                    className="palette-modal-box-header__left-action"
                    iconColor="black"
                    iconName={leftActionIcon}
                    size="x-small"
                    theme="secondary"
                    onClick={onLeftActionClick}
                />
            )}
            <div className="palette-modal-box-header__main-section">
                {title && (
                    <div className={classNames('palette-modal-box-header__heading',
                        `palette-modal-box-header__heading--align-${titleAlign}`)}
                    >
                        <Header
                            className="palette-modal-box-header__title palette-modal-box-header__title--desktop"
                            color="strong"
                            id="modal-title"
                            level="4"
                        >
                            {title}
                        </Header>
                        <BodyText
                            className="palette-modal-box-header__title palette-modal-box-header__title--mobile"
                            color="strong"
                            level="1"
                        >
                            {title}
                        </BodyText>
                        {subtitle && (
                            <Small color="light">{subtitle}</Small>
                        )}
                    </div>
                )}
                {children}
            </div>
            <Button
                isTransparent
                ariaLabel={translate('close', null, null, 'Close')}
                className="palette-modal-box-header__close-button"
                iconName="palette_close"
                size="large"
                theme="secondary"
                onClick={handleCloseButtonClick}
            />
        </header>
    );
}

export default ModalBoxHeader;
